'use client';

import { doc, getFirestore, serverTimestamp, getDoc } from 'firebase/firestore';
import { setDocumentNonBlocking } from '@/firebase/non-blocking-updates';
import { z } from 'zod';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const UserProfileSchema = z.object({
  id: z.string(),
  email: z.string().email(),
  name: z.string(),
  locale: z.string(),
  planTier: z.enum(['free', 'starter', 'business', 'agency']),
  creditsBalance: z.number(),
  createdAt: z.string().datetime(),
  lastLoginAt: z.string().datetime(),
});

export type UserProfile = z.infer<typeof UserProfileSchema>;

/**
 * Creates or updates a user's profile in Firestore.
 * This function uses a non-blocking write.
 * @param uid - The user's unique ID from Firebase Auth.
 * @param userData - The user profile data.
 * @returns {Promise<void>} A promise that resolves when the write is initiated.
 */
export function createUserProfile(uid: string, userData: Omit<UserProfile, 'id' | 'createdAt' | 'lastLoginAt'>): Promise<void> {
  const firestore = getFirestore();
  const userDocRef = doc(firestore, 'users', uid);

  const initializeProfile = async () => {
    const snapshot = await getDoc(userDocRef);

    if (!snapshot.exists()) {
      const profileData = {
        ...userData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        lastLoginAt: serverTimestamp(),
      };

      return setDocumentNonBlocking(userDocRef, profileData, { merge: false });
    }

    const existing = snapshot.data() as Partial<UserProfile>;

    const updates: Record<string, unknown> = {
      email: userData.email,
      name: userData.name,
      locale: userData.locale,
      planTier: existing.planTier ?? userData.planTier,
      updatedAt: serverTimestamp(),
      lastLoginAt: serverTimestamp(),
    };

    return setDocumentNonBlocking(userDocRef, updates, { merge: true });
  };

  return initializeProfile();
}
